import { z } from 'zod';

// Final Client Details Form — shareable traveller-information form generated
// from an itinerary once its status passes the proposal phase.
// PRD: apps/itineraries/prd/final-client-details-form.md
// The link is the credential: anyone holding it can read and write.

// ── Status gate ────────────────────────────────────────────────────────
//
// Availability is decided by the itinerary status's SALES STAGE, so the feature
// works in every organization with no per-org configuration.
//
// Statuses are per-organization rows that orgs name freely, so an earlier
// name / `tc_status` match only ever worked for one org's catalogue. The stage
// is the one numeric property every org's catalogue shares.
//
// The band is `> PROPOSAL_STAGE_CEILING` (6) and `< CANCELLED_STAGE` (20): past
// the proposal phase, and not cancelled. Proposal currently sits at 4, so 5-6
// stay free for further proposal-side stages and 7-9 for post-sale ones.

/** Statuses at or below this stage are still in the proposal phase. */
const PROPOSAL_STAGE_CEILING = 6;

/** Cancelled — out of the pipeline, so no form. */
const CANCELLED_STAGE = 20;

/**
 * True when this status's sales stage makes the Final Client Details Form
 * available.
 *
 * Per spec §4.4 this gates CREATION only: an already-generated form keeps
 * working when the status later moves backwards, so read paths never call it.
 *
 * A status with no stage (`null`) is outside the band — those are the
 * Excluded / Sample / Training rows, which never reach suppliers.
 */
export const isFinalClientFormStatus = (input: {
  salesStage?: number | string | null;
}): boolean => {
  const raw = input.salesStage;
  if (raw === null || raw === undefined || raw === '') return false;
  // `sales_stage` is a PG `numeric`, so it arrives as a string on some paths.
  const stage = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(stage)) return false;
  return stage > PROPOSAL_STAGE_CEILING && stage < CANCELLED_STAGE;
};

// ── Share-link upload header ───────────────────────────────────────────

/**
 * Header the public form's uploads use to authorise against the share token.
 * The UploadThing handler has no tRPC context, so the token rides a header
 * (resolved in apps/api/src/shared/uploadthing.ts).
 *
 * Lives HERE, not beside the hashing helpers in `@travel-planner/api-shared`:
 * that module imports `node:crypto`, and the browser bundle must not pull it in
 * just to read a header name.
 */
export const FINAL_CLIENT_FORM_TOKEN_HEADER = 'x-final-client-form-token';

// ── Experience catalogue (spec §3) ─────────────────────────────────────

export const finalClientFormExperienceSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  /** What each selected experience needs per traveller, e.g. "weight and height". */
  requiredData: z.string().min(1),
});
export type FinalClientFormExperience = z.infer<
  typeof finalClientFormExperienceSchema
>;

// ── Form entities ──────────────────────────────────────────────────────

export const finalClientFormFlightSchema = z.object({
  flight: z.string(),
  fromAirport: z.string(),
  toAirport: z.string(),
  date: z.string(),
  time: z.string(),
});
export type FinalClientFormFlight = z.infer<typeof finalClientFormFlightSchema>;

export const finalClientFormFlightsSchema = z.object({
  arrival: finalClientFormFlightSchema,
  departure: finalClientFormFlightSchema,
});
export type FinalClientFormFlights = z.infer<
  typeof finalClientFormFlightsSchema
>;

export const EMPTY_FLIGHT: FinalClientFormFlight = {
  flight: '',
  fromAirport: '',
  toAirport: '',
  date: '',
  time: '',
};

export const EMPTY_FLIGHTS: FinalClientFormFlights = {
  arrival: { ...EMPTY_FLIGHT },
  departure: { ...EMPTY_FLIGHT },
};

/** Passport scan / data page. UploadThing key + display name. */
export const finalClientFormDocumentSchema = z.object({
  key: z.string().min(1),
  name: z.string(),
  url: z.string(),
});
export type FinalClientFormDocument = z.infer<
  typeof finalClientFormDocumentSchema
>;

// ── Passport details ───────────────────────────────────────────────────

/** Uppercase, with spaces and dashes removed — how the number is printed. */
export const normalisePassportNumber = (value: string): string =>
  value.replace(/[\s-]/g, '').toUpperCase();

/**
 * ICAO 9303 document numbers are at most 9 characters, letters and digits.
 * National formats sit between 6 and 9, so anything outside that is flagged.
 */
export const isValidPassportNumber = (value: string): boolean =>
  /^[A-Z0-9]{6,9}$/.test(normalisePassportNumber(value));

/** True when an ISO expiry date is before `today` (both yyyy-mm-dd, UTC). */
export const isPassportExpired = (
  expiry: string | null | undefined,
  today: string
): boolean => Boolean(expiry) && (expiry as string) < today;

export const finalClientFormTravellerSchema = z.object({
  id: z.uuid(),
  position: z.number().int(),
  fullName: z.string(),
  /** ISO date (yyyy-mm-dd) or null. Age is derived from it, never stored. */
  dateOfBirth: z.string().nullable(),
  /** Age as XT held it when no date of birth exists (spec §5). Display only. */
  recordedAge: z.string().nullable(),
  phone: z.string(),
  /** Legacy passport scans — the form now asks for passport details instead. */
  passportDocuments: z.array(finalClientFormDocumentSchema),
  /** ISO 3166-1 alpha-2 issuing country, or null. */
  passportCountry: z.string().nullable(),
  passportNumber: z.string(),
  /** ISO date (yyyy-mm-dd) or null. */
  passportExpiry: z.string().nullable(),
  dietaryRestrictions: z.string().nullable(),
  mobilityRequirements: z.string().nullable(),
  specificRequests: z.string().nullable(),
  flights: finalClientFormFlightsSchema,
  /** Field names pre-filled from our records — drives the "from our records" marker. */
  prefilledFields: z.array(z.string()),
  flightPromptDismissed: z.boolean(),
});
export type FinalClientFormTraveller = z.infer<
  typeof finalClientFormTravellerSchema
>;

export const finalClientFormBedPreference = z.enum(['Twin', 'Double']);
export type FinalClientFormBedPreference = z.infer<
  typeof finalClientFormBedPreference
>;

export const BED_PREFERENCE_NOTE = 'subject to availability';

export const finalClientFormRoomSchema = z.object({
  id: z.uuid(),
  position: z.number().int(),
  bedPreference: finalClientFormBedPreference.nullable(),
  travellers: z.array(finalClientFormTravellerSchema),
});
export type FinalClientFormRoom = z.infer<typeof finalClientFormRoomSchema>;

export const finalClientFormStatus = z.enum(['draft', 'submitted']);
export type FinalClientFormStatus = z.infer<typeof finalClientFormStatus>;

/** The whole form as the public page reads it. */
export const finalClientFormSchema = z.object({
  id: z.uuid(),
  itineraryId: z.uuid(),
  itineraryTitle: z.string().nullable(),
  itineraryRef: z.string().nullable(),
  status: finalClientFormStatus,
  headerImageUrl: z.string().nullable(),
  /** Selected experience keys, resolved against the catalogue. */
  experiences: z.array(z.string()),
  /** The catalogue rows for the selected keys — label + required data. */
  experienceCatalogue: z.array(finalClientFormExperienceSchema),
  rooms: z.array(finalClientFormRoomSchema),
  updatedOn: z.string().nullable(),
  submittedAt: z.string().nullable(),
  lastSavedBy: z.string().nullable(),
});
export type FinalClientForm = z.infer<typeof finalClientFormSchema>;

// ── Consultant-side inputs / outputs ──────────────────────────────────

export const generateFinalClientFormInput = z.object({
  itineraryId: z.uuid(),
});
export type GenerateFinalClientFormInput = z.infer<
  typeof generateFinalClientFormInput
>;

/**
 * Link meta for the settings button. `rawToken`/`url` are present only on the
 * call that minted them (generate / revokeAndReissue) — `getByItinerary` never
 * re-exposes the secret, so the consultant re-issues if the link is lost.
 */
export const finalClientFormLinkOutput = z.object({
  formId: z.uuid().nullable(),
  itineraryId: z.uuid(),
  exists: z.boolean(),
  url: z.string().nullable(),
  status: finalClientFormStatus.nullable(),
  updatedOn: z.string().nullable(),
  submittedAt: z.string().nullable(),
  /** Completion summary for the settings button (spec §4.3). */
  completion: z
    .object({
      complete: z.boolean(),
      travellerCount: z.number().int(),
      open: z.array(z.string()),
    })
    .nullable(),
});
export type FinalClientFormLinkOutput = z.infer<
  typeof finalClientFormLinkOutput
>;

export const getFinalClientFormInput = z.object({
  itineraryId: z.uuid(),
});

export const refreshFinalClientFormInput = z.object({
  itineraryId: z.uuid(),
});

// ── Public (token) inputs ─────────────────────────────────────────────

/** Every public path is addressed by form id + raw token. */
export const finalClientFormTokenInput = z.object({
  formId: z.uuid(),
  token: z.string().min(1),
});
export type FinalClientFormTokenInput = z.infer<
  typeof finalClientFormTokenInput
>;

/**
 * Per-FIELD save (spec §6): two people may hold the link at once, so a late
 * save must not overwrite the other person's unrelated edits. Never send the
 * whole document.
 */
export const saveFinalClientFormFieldInput = finalClientFormTokenInput.extend({
  travellerId: z.uuid().optional(),
  roomId: z.uuid().optional(),
  field: z.string().min(1),
  value: z.unknown(),
  /** The name the filler gave, if any — recorded on the save log (§10). */
  savedBy: z.string().max(200).optional(),
});
export type SaveFinalClientFormFieldInput = z.infer<
  typeof saveFinalClientFormFieldInput
>;

export const finalClientFormRoomInput = finalClientFormTokenInput.extend({
  roomId: z.uuid(),
});

export const finalClientFormTravellerInput = finalClientFormTokenInput.extend({
  travellerId: z.uuid(),
});

export const copyFinalClientFormFlightsInput = finalClientFormTokenInput.extend(
  {
    travellerId: z.uuid(),
    fromTravellerId: z.uuid(),
  }
);

export const addFinalClientFormTravellerInput =
  finalClientFormTokenInput.extend({
    roomId: z.uuid(),
  });

// ── Submit payload contract (spec §8) ─────────────────────────────────
//
// Field names ARE the contract between the form and the itinerary software, so
// these keys are snake_case on the wire on purpose — do not camelCase them.
// Keys are always present; unknown values are empty strings or null.

export const FINAL_CLIENT_FORM_PAYLOAD_NAME = 'tempovip.traveller_information';
export const FINAL_CLIENT_FORM_PAYLOAD_VERSION = 3;

export const finalClientFormPayloadFlightSchema = z.object({
  flight: z.string(),
  from_airport: z.string(),
  to_airport: z.string(),
  date: z.string(),
  time: z.string(),
});

export const finalClientFormPayloadPassengerSchema = z.object({
  full_name: z.string(),
  date_of_birth: z.string(),
  age: z.string(),
  phone: z.string(),
  passport_documents: z.array(z.string()),
  passport: z.object({
    country: z.string(),
    number: z.string(),
    expiry_date: z.string(),
  }),
  dietary_restrictions: z.string().nullable(),
  mobility_requirements: z.string().nullable(),
  specific_requests: z.string().nullable(),
  flights: z.object({
    arrival: finalClientFormPayloadFlightSchema,
    departure: finalClientFormPayloadFlightSchema,
  }),
});

export const finalClientFormPayloadRoomSchema = z.object({
  room: z.number().int(),
  bed_preference: z.string().nullable(),
  bed_preference_note: z.string(),
  occupancy: z.number().int(),
  passengers: z.array(finalClientFormPayloadPassengerSchema),
});

export const finalClientFormPayloadSchema = z.object({
  form: z.literal(FINAL_CLIENT_FORM_PAYLOAD_NAME),
  version: z.literal(FINAL_CLIENT_FORM_PAYLOAD_VERSION),
  itinerary_id: z.string(),
  experiences: z.array(z.string()),
  rooming_list: z.array(finalClientFormPayloadRoomSchema),
});
export type FinalClientFormPayload = z.infer<
  typeof finalClientFormPayloadSchema
>;

export const submitFinalClientFormOutput = z.object({
  formId: z.uuid(),
  submittedAt: z.string(),
  payload: finalClientFormPayloadSchema,
  /**
   * Write-backs that could not be applied (spec §11: "skip that write-back and
   * flag it to operations rather than failing the submission").
   */
  skipped: z.array(z.string()),
});
export type SubmitFinalClientFormOutput = z.infer<
  typeof submitFinalClientFormOutput
>;
